import React, { useMemo, useState } from "react";
import {
  List,
  Filter,
  Datagrid,
  ReferenceInput,
  DateField,
  DateInput,
  SelectInput,
  EditButton,
  TextInput,
  TextField,
  NumberField,
  Pagination,
  useDataProvider,
  DataProviderContext,
  Button,
  useRecordContext,
} from "react-admin";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
} from "@mui/material";
import { Visibility, Close } from "@mui/icons-material";
import * as XLSX from "xlsx";
import DownloadButton from "../common/DownloadButton/DownloadButton";

const ADMIN_ROLE = "winco";
const SERVICE_ADMIN_ROLE = "serviceadmin";

const ViewInvoiceButton = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const record = useRecordContext();

  if (!record || !record.invoiceImage) {
    return null;
  }

  // Función para detectar si es PDF
  const isPdf = (url) => {
    return url.toLowerCase().includes('.pdf') || url.toLowerCase().includes('pdf');
  };

  // Función para detectar si es imagen
  const isImage = (url) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    return imageExtensions.some(ext => url.toLowerCase().includes(ext)) || 
           url.toLowerCase().includes('image');
  };

  const fileUrl = record.invoiceImage;
  const isPdfFile = isPdf(fileUrl);
  const isImageFile = isImage(fileUrl);

  return (
    <>
      <Button
        onClick={handleOpen}
        label="Ver Factura"
        color="primary"
        variant="outlined"
        size="small"
        startIcon={<Visibility />}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: {
            maxHeight: "95vh",
            height: isPdfFile ? "95vh" : "auto",
          },
        }}
      >
        <DialogTitle>
          Factura {isPdfFile ? "(PDF)" : "(Imagen)"}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
            height: isPdfFile ? "calc(95vh - 120px)" : "auto",
          }}
        >
          {isPdfFile ? (
            // Visualizador de PDF
            <iframe
              src={fileUrl}
              title="Factura PDF"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: "70vh",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
          ) : isImageFile ? (
            // Visualizador de imagen
            <img
              src={fileUrl}
              alt="Factura"
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
          ) : (
            // Fallback: intentar mostrar como imagen primero, luego como PDF
            <>
              <img
                src={fileUrl}
                alt="Factura"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
              <iframe
                src={fileUrl}
                title="Factura"
                style={{
                  display: "none",
                  width: "100%",
                  height: "70vh",
                  border: "none",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
            </>
          )}
          <div
            style={{
              display: "none",
              textAlign: "center",
              padding: "20px",
              color: "#666",
            }}
          >
            No se pudo cargar el archivo de la factura
            <br />
            <a 
              href={fileUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              Abrir en nueva pestaña
            </a>
          </div>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => window.open(fileUrl, "_blank")} 
            label="Abrir en nueva pestaña" 
            color="secondary"
          />
          <Button onClick={handleClose} label="Cerrar" />
        </DialogActions>
      </Dialog>
    </>
  );
};

const ServiceRequestsTitle = () => {
  return (
    <div
      style={{
        display: "flex",
        width: 500,
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      Pedidos de Servicio Técnico
      {[ADMIN_ROLE, SERVICE_ADMIN_ROLE].includes(
        localStorage.getItem("role")
      ) ? (
        <DownloadButton resource="servicerequest" />
      ) : null}
    </div>
  );
};

const ServiceRequestsFilter = (props) => (
  <Filter {...props}>
    {/* <TextInput label="ID del Pedido" source="id" alwaysOn /> */}
    <TextInput label="Email del Cliente" source="clientEmail" alwaysOn />
    <DateInput label="Fecha desde" source="createdAtGte" />
    <DateInput label="Fecha hasta" source="createdAtLte" />
    <TextInput label="Estado" source="status" />
    <ReferenceInput
      label="Servicio"
      source="serviceId"
      reference="services"
      perPage={1000}
      sort={{ field: "name", order: "ASC" }}
    >
      <SelectInput optionText="name" label="Servicio" />
    </ReferenceInput>
  </Filter>
);

// Función personalizada de exportación
const serviceRequestsExporter = (data) => {
  const exportData = data.map((record) => ({
    ID: record.id,
    "Fecha de Creación": new Date(record.createdAt).toLocaleDateString("es-AR"),
    Estado: record.status || "-",
    Servicio: record.service?.name || "-",
    Producto: record.product?.fullName || "-",
    "Estado del Producto": record.productStatus || "-",
    "Detalles de Repuestos": record.sparePartsDetail || "-",
    Comentarios: record.comments || "-",
    "Email del Cliente": record.clientEmail || "-",
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  const colWidths = Object.keys(exportData[0]).map((h) => ({
    wch: Math.max(h.length, 20),
  }));
  ws["!cols"] = colWidths;

  XLSX.utils.book_append_sheet(wb, ws, "Pedidos de Servicio");
  XLSX.writeFile(
    wb,
    `pedidos-servicio-${new Date().toISOString().split("T")[0]}.xlsx`
  );
};

const ServiceRequestsListView = (props) => (
  <List
    filters={<ServiceRequestsFilter />}
    title={<ServiceRequestsTitle />}
    {...props}
    sort={{ field: "id", order: "DESC" }}
    pagination={<Pagination rowsPerPageOptions={[5, 10, 25]} />}
    exporter={serviceRequestsExporter}
  >
    <Datagrid bulkActionButtons={false} rowClick={false}>
      <NumberField source="id" label="ID" />
      <DateField source="createdAt" locales="es-AR" label="Fecha de Creación" />
      <TextField source="status" label="Estado" />
      {[ADMIN_ROLE, SERVICE_ADMIN_ROLE].includes(
        localStorage.getItem("role")
      ) && <TextField source="service.name" label="Servicio" />}
      <TextField source="product.fullName" label="Producto" />
      <TextField source="productStatus" label="Estado del Producto" />
      <TextField source="sparePartsDetail" label="Detalles de Repuestos" />
      <TextField source="comments" label="Comentarios" />
      <TextField source="clientEmail" label="Email del Cliente" />
      <ViewInvoiceButton />
      {["winco", "serviceadmin", "service"].includes(
        localStorage.getItem("role")
      ) && <EditButton label="Editar" />}
    </Datagrid>
  </List>
);

export const ServiceRequestsList = (props) => {
  const dataProvider = useDataProvider();

  const transformedDataProvider = useMemo(
    () => ({
      ...dataProvider,
      getList: (resource, params) => {
        if (resource === "servicerequests" && params.filter) {
          const {
            createdAtGte,
            createdAtLte,
            clientEmail,
            status,
            serviceId,
            ...restFilter
          } = params.filter;

          const newFilter = { ...restFilter };

          if (clientEmail) {
            newFilter.clientEmail = { like: `%${clientEmail}%` };
          }
          if (status) {
            newFilter.status = { like: `%${status}%` };
          }
          if (serviceId) {
            newFilter.service = serviceId;
          }

          if (createdAtGte || createdAtLte) {
            newFilter.createdAt = {};
            if (createdAtGte) {
              const gteDate = new Date(createdAtGte);
              gteDate.setUTCHours(0, 0, 0, 0);
              newFilter.createdAt[">="] = gteDate.toISOString();
            }
            if (createdAtLte) {
              const lteDate = new Date(createdAtLte);
              lteDate.setUTCHours(23, 59, 59, 999);
              newFilter.createdAt["<="] = lteDate.toISOString();
            }
          }

          return dataProvider.getList(resource, {
            ...params,
            filter: newFilter,
          });
        }
        return dataProvider.getList(resource, params);
      },
    }),
    [dataProvider]
  );

  return (
    <DataProviderContext.Provider value={transformedDataProvider}>
      <ServiceRequestsListView {...props} />
    </DataProviderContext.Provider>
  );
};
